<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Please provide the legal information for this trust account, including
          the trust's full legal name, tax identification, formation details, and intended purpose.
        </p>
      </div>
    </help-drawer>
    <v-form name="PershingTrustInfo" class="form" data-test-id="pershing-trust-info-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.PERSHING_TRUST_INFO.id" :instance="instance"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="pershing-trust-info-header">
              <span class="headerlight">
                Provide <strong>trust information</strong> for
                <strong>{{ label }}</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving trust information...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isPershingTrustInfoComplete && formHidden && !isSyncing"
          :submitted="isPershingTrustInfoComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-row class="justify-center" v-if="!formHidden && !isSyncing">
          <v-col class="step-form-container large">
            
            <!-- Trust Legal Name -->
            <label for="fullLegalName" class="required" data-test-id="full-legal-name-label">
              Full Legal Name of Trust
            </label>
            <v-text-field
              outlined
              type="text"
              maxlength="256"
              name="fullLegalName"
              aria-required="true"
              v-model.trim="trustInfo.fullLegalName"
              :backgroundColor="inputBackgroundColor"
              @input="$v.trustInfo.fullLegalName.$touch()"
              @blur="$v.trustInfo.fullLegalName.$touch()"
              :error-messages="fullLegalNameErrors"
              hide-details="auto"
              class="mb-4"
              data-test-id="full-legal-name-input"
            />
            
            <!-- Trust Intended Purpose -->
            <label for="trustIntendedPurpose" class="required" data-test-id="trust-intended-purpose-label">
              Trust Intended Purpose
            </label>
            <v-select
              v-model="trustInfo.trustIntendedPurpose"
              :items="trustIntendedPurposeOptions"
              :backgroundColor="inputBackgroundColor"
              outlined
              dense
              hide-details="auto"
              placeholder="Choose Intended Purpose"
              @change="$v.trustInfo.trustIntendedPurpose.$touch()"
              :error-messages="trustIntendedPurposeErrors"
              data-test-id="trust-intended-purpose-select"
              class="mb-4"
            />
            
            <!-- Tax ID Type and Number -->
            <v-row>
              <v-col cols="12" sm="6">
                <label for="taxIdType" class="required" data-test-id="tax-id-type-label">
                  Tax ID Type
                </label>
                <v-select
                  v-model="trustInfo.taxIdType"
                  :items="taxIdTypes"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  @change="$v.trustInfo.taxIdType.$touch()"
                  :error-messages="taxIdTypeErrors"
                  data-test-id="tax-id-type-select"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <label for="taxId" class="required" data-test-id="tax-id-label">
                  Tax ID Number
                </label>
                <v-text-field
                  outlined
                  type="text"
                  :maxlength="trustInfo.taxIdType === 'SSN' ? 11 : 10"
                  name="taxId"
                  aria-required="true"
                  v-model="trustInfo.taxId"
                  :placeholder="trustInfo.taxIdType === 'SSN' ? '###-##-####' : '##-#######'"
                  :backgroundColor="inputBackgroundColor"
                  @input="formatTaxId"
                  @blur="$v.trustInfo.taxId.$touch()"
                  :error-messages="taxIdErrors"
                  hide-details="auto"
                  data-test-id="tax-id-input"
                />
              </v-col>
            </v-row>
            
            <!-- Formation Details -->
            <h3 class="mt-6 mb-4">Formation Details</h3>
            
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <label for="dateOfFormation" class="required" data-test-id="formation-date-label">
                  Date of Formation
                </label>
                <v-text-field
                  v-model="trustInfo.dateOfFormation"
                  :placeholder="INPUT_DATE_FMT"
                  outlined
                  name="dateOfFormation"
                  maxlength="8"
                  :backgroundColor="inputBackgroundColor"
                  @input="$v.trustInfo.dateOfFormation.$touch()"
                  @blur="standardizeDateOfFormation()"
                  :error-messages="dateOfFormationErrors"
                  hide-details="auto"
                  inputmode="numeric"
                  data-test-id="formation-date-input"
                >
                  <template v-slot:append>
                    <span class="cal-icon" data-test-id="calendar-icon">
                      <v-icon small color="#9b9b9b">
                        fas fa-calendar-alt
                      </v-icon>
                    </span>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <label for="stateOfOrganization" class="required" data-test-id="state-org-label">
                  State of Organization
                </label>
                <v-select
                  v-model="trustInfo.stateOfOrganization"
                  :items="states"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  @change="$v.trustInfo.stateOfOrganization.$touch()"
                  :error-messages="stateOfOrganizationErrors"
                  data-test-id="state-org-select"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <label for="countryOfOrganization" class="required" data-test-id="country-org-label">
                  Country of Organization
                </label>
                <v-select
                  v-model="trustInfo.countryOfOrganization"
                  :items="countries"
                  :backgroundColor="inputBackgroundColor"
                  outlined
                  dense
                  hide-details="auto"
                  @change="$v.trustInfo.countryOfOrganization.$touch()"
                  :error-messages="countryOfOrganizationErrors"
                  data-test-id="country-org-select"
                />
              </v-col>
            </v-row>
            
            <!-- Trust Details Checkboxes (all in one row) -->
            <h3 class="mt-6 mb-4">Trust Details</h3>
            
            <v-row>
              <v-col cols="12" sm="4">
                <label class="required mb-2 d-block" data-test-id="all-parties-label">
                  Are all parties of this Trust individuals?
                </label>
                <v-radio-group
                  v-model="trustInfo.allPartiesIndividuals"
                  :error-messages="allPartiesIndividualsErrors"
                  hide-details="auto"
                  row
                  @change="$v.trustInfo.allPartiesIndividuals.$touch()"
                  data-test-id="all-parties-radio-group"
                  class="mt-1"
                >
                  <v-radio
                    :ripple="false"
                    label="Yes"
                    :value="true"
                    data-test-id="all-parties-yes"
                  ></v-radio>
                  <v-radio
                    :ripple="false"
                    label="No"
                    :value="false"
                    data-test-id="all-parties-no"
                    class="ml-4"
                  ></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="required mb-2 d-block" data-test-id="statutory-trust-label">
                  Is this a statutory trust?
                </label>
                <v-radio-group
                  v-model="trustInfo.statutoryTrust"
                  :error-messages="statutoryTrustErrors"
                  hide-details="auto"
                  row
                  @change="$v.trustInfo.statutoryTrust.$touch()"
                  data-test-id="statutory-trust-radio-group"
                  class="mt-1"
                >
                  <v-radio
                    :ripple="false"
                    label="Yes"
                    :value="true"
                    data-test-id="statutory-trust-yes"
                  ></v-radio>
                  <v-radio
                    :ripple="false"
                    label="No"
                    :value="false"
                    data-test-id="statutory-trust-no"
                    class="ml-4"
                  ></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="required mb-2 d-block" data-test-id="address-same-label">
                  Is the trust address the same as the individual's address?
                </label>
                <v-radio-group
                  v-model="trustInfo.addressSameAsOwner"
                  :error-messages="addressSameAsOwnerErrors"
                  hide-details="auto"
                  row
                  @change="$v.trustInfo.addressSameAsOwner.$touch()"
                  data-test-id="address-same-radio-group"
                  class="mt-1"
                >
                  <v-radio
                    :ripple="false"
                    label="Yes"
                    :value="true"
                    data-test-id="address-same-yes"
                  ></v-radio>
                  <v-radio
                    :ripple="false"
                    label="No"
                    :value="false"
                    data-test-id="address-same-no"
                    class="ml-4"
                  ></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            
            <!-- Business Address (conditional) -->
            <v-expand-transition>
              <div v-if="trustInfo.addressSameAsOwner === false">
                <h3 class="mt-6 mb-4">Business Address</h3>
                
                <label for="street" class="required" data-test-id="street-label">
                  Street Address
                </label>
                <v-text-field
                  outlined
                  type="text"
                  maxlength="256"
                  name="street"
                  aria-required="true"
                  v-model.trim="trustInfo.businessAddress.street"
                  :backgroundColor="inputBackgroundColor"
                  @input="$v.trustInfo.businessAddress.street.$touch()"
                  @blur="$v.trustInfo.businessAddress.street.$touch()"
                  :error-messages="businessAddressStreetErrors"
                  hide-details="auto"
                  class="mb-4"
                  data-test-id="street-input"
                />
                
                <v-row>
                  <v-col cols="12" sm="6">
                    <label for="city" class="required" data-test-id="city-label">
                      City
                    </label>
                    <v-text-field
                      outlined
                      type="text"
                      maxlength="256"
                      name="city"
                      aria-required="true"
                      v-model.trim="trustInfo.businessAddress.city"
                      :backgroundColor="inputBackgroundColor"
                      @input="$v.trustInfo.businessAddress.city.$touch()"
                      @blur="$v.trustInfo.businessAddress.city.$touch()"
                      :error-messages="businessAddressCityErrors"
                      hide-details="auto"
                      data-test-id="city-input"
                    />
                  </v-col>
                  <v-col cols="12" sm="3">
                    <label for="state" class="required" data-test-id="state-label">
                      State
                    </label>
                    <v-select
                      v-model="trustInfo.businessAddress.state"
                      :items="states"
                      :backgroundColor="inputBackgroundColor"
                      outlined
                      dense
                      hide-details="auto"
                      @change="$v.trustInfo.businessAddress.state.$touch()"
                      :error-messages="businessAddressStateErrors"
                      data-test-id="state-select"
                    />
                  </v-col>
                  <v-col cols="12" sm="3">
                    <label for="zipCode" class="required" data-test-id="zip-label">
                      ZIP Code
                    </label>
                    <v-text-field
                      outlined
                      type="text"
                      maxlength="10"
                      name="zipCode"
                      aria-required="true"
                      v-model="trustInfo.businessAddress.zipCode"
                      :backgroundColor="inputBackgroundColor"
                      @input="$v.trustInfo.businessAddress.zipCode.$touch()"
                      @blur="$v.trustInfo.businessAddress.zipCode.$touch()"
                      :error-messages="businessAddressZipErrors"
                      hide-details="auto"
                      data-test-id="zip-input"
                    />
                  </v-col>
                </v-row>
                
                <v-row class="mt-2">
                  <v-col cols="12" sm="6">
                    <label for="country" class="required" data-test-id="country-label">
                      Country
                    </label>
                    <v-select
                      v-model="trustInfo.businessAddress.country"
                      :items="countries"
                      :backgroundColor="inputBackgroundColor"
                      outlined
                      dense
                      hide-details="auto"
                      @change="$v.trustInfo.businessAddress.country.$touch()"
                      :error-messages="businessAddressCountryErrors"
                      data-test-id="country-select"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
            
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="pershing-trust-info-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS, INPUT_DATE_FMT, STATES, COUNTRIES, validInputDate, formatDate, US_DATE_SHORT } from '@/common/constants';
import { required, requiredIf } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'PershingTrustInfo',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],
  
  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    trustInfo: {
      fullLegalName: null,
      trustIntendedPurpose: null,
      taxIdType: null,
      taxId: null,
      allPartiesIndividuals: null,
      statutoryTrust: null,
      addressSameAsOwner: null,
      businessAddress: {
        street: null,
        city: null,
        state: null,
        zipCode: null,
        country: 'US',
      },
      dateOfFormation: null,
      stateOfOrganization: null,
      countryOfOrganization: 'US',
    },
    taxIdTypes: [
      { text: 'SSN', value: 'SSN' },
      { text: 'TIN', value: 'TIN' },
    ],
    states: STATES,
    countries: COUNTRIES,
    trustIntendedPurposeOptions: [
      { text: 'Estate/Tax Planning', value: 'Estate/Tax Planning' },
      { text: 'Employee Benefit Plan', value: 'Employee Benefit Plan' },
    ],
    MENUS: MENUS,
    INPUT_DATE_FMT: INPUT_DATE_FMT,
  }),
  
  validations() {
    const validations = {
      trustInfo: {
        fullLegalName: { required },
        trustIntendedPurpose: { required },
        taxIdType: { required },
        taxId: { 
          required,
          validTaxId(value) {
            if (!value) return true;
            const cleaned = value.replace(/[^0-9]/g, '');
            if (this.trustInfo.taxIdType === 'SSN') {
              return cleaned.length === 9;
            } else if (this.trustInfo.taxIdType === 'TIN') {
              return cleaned.length === 9;
            }
            return false;
          }
        },
        allPartiesIndividuals: { required },
        statutoryTrust: { required },
        addressSameAsOwner: { required },
        businessAddress: {
          street: { 
            required: requiredIf(function() {
              return this.trustInfo.addressSameAsOwner === false;
            })
          },
          city: { 
            required: requiredIf(function() {
              return this.trustInfo.addressSameAsOwner === false;
            })
          },
          state: { 
            required: requiredIf(function() {
              return this.trustInfo.addressSameAsOwner === false;
            })
          },
          zipCode: { 
            required: requiredIf(function() {
              return this.trustInfo.addressSameAsOwner === false;
            })
          },
          country: { 
            required: requiredIf(function() {
              return this.trustInfo.addressSameAsOwner === false;
            })
          },
        },
        dateOfFormation: { 
          required,
          validInputDate,
        },
        stateOfOrganization: { required },
        countryOfOrganization: { required },
      },
      validationGroup: ['trustInfo'],
    };
    
    return validations;
  },
  
  computed: {
    instance() {
      return {
        name: this.name,
        label: this.label,
      };
    },
    
    isPershingTrustInfoComplete() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_TRUST_INFO.id, this.instance);
      return page && page.lastCompleted;
    },
    
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_TRUST_INFO.id, this.instance);
      return page && page.status === 'IN_PROGRESS';
    },
    
    helpTitle() {
      return 'What does this mean?';
    },
    
    // Error messages
    fullLegalNameErrors() {
      const errors = [];
      if (!this.$v.trustInfo.fullLegalName.$dirty) return errors;
      !this.$v.trustInfo.fullLegalName.required && errors.push('Full legal name is required');
      return errors;
    },
    
    trustIntendedPurposeErrors() {
      const errors = [];
      if (!this.$v.trustInfo.trustIntendedPurpose.$dirty) return errors;
      !this.$v.trustInfo.trustIntendedPurpose.required && errors.push('Trust intended purpose is required');
      return errors;
    },
    
    taxIdTypeErrors() {
      const errors = [];
      if (!this.$v.trustInfo.taxIdType.$dirty) return errors;
      !this.$v.trustInfo.taxIdType.required && errors.push('Tax ID type is required');
      return errors;
    },
    
    taxIdErrors() {
      const errors = [];
      if (!this.$v.trustInfo.taxId.$dirty) return errors;
      !this.$v.trustInfo.taxId.required && errors.push('Tax ID is required');
      !this.$v.trustInfo.taxId.validTaxId && errors.push('Please enter a valid tax ID');
      return errors;
    },
    
    allPartiesIndividualsErrors() {
      const errors = [];
      if (!this.$v.trustInfo.allPartiesIndividuals.$dirty) return errors;
      !this.$v.trustInfo.allPartiesIndividuals.required && errors.push('This field is required');
      return errors;
    },
    
    statutoryTrustErrors() {
      const errors = [];
      if (!this.$v.trustInfo.statutoryTrust.$dirty) return errors;
      !this.$v.trustInfo.statutoryTrust.required && errors.push('This field is required');
      return errors;
    },
    
    addressSameAsOwnerErrors() {
      const errors = [];
      if (!this.$v.trustInfo.addressSameAsOwner.$dirty) return errors;
      !this.$v.trustInfo.addressSameAsOwner.required && errors.push('This field is required');
      return errors;
    },
    
    dateOfFormationErrors() {
      const errors = [];
      if (!this.$v.trustInfo.dateOfFormation.$dirty) return errors;
      !this.$v.trustInfo.dateOfFormation.required && errors.push('Date of formation is required');
      !this.$v.trustInfo.dateOfFormation.validInputDate && errors.push('Date must be valid (MMDDYYYY)');
      return errors;
    },
    
    stateOfOrganizationErrors() {
      const errors = [];
      if (!this.$v.trustInfo.stateOfOrganization.$dirty) return errors;
      !this.$v.trustInfo.stateOfOrganization.required && errors.push('State of organization is required');
      return errors;
    },
    
    countryOfOrganizationErrors() {
      const errors = [];
      if (!this.$v.trustInfo.countryOfOrganization.$dirty) return errors;
      !this.$v.trustInfo.countryOfOrganization.required && errors.push('Country of organization is required');
      return errors;
    },
    
    // Business Address errors
    businessAddressStreetErrors() {
      const errors = [];
      if (!this.$v.trustInfo.businessAddress.street.$dirty) return errors;
      !this.$v.trustInfo.businessAddress.street.required && errors.push('Street address is required');
      return errors;
    },
    
    businessAddressCityErrors() {
      const errors = [];
      if (!this.$v.trustInfo.businessAddress.city.$dirty) return errors;
      !this.$v.trustInfo.businessAddress.city.required && errors.push('City is required');
      return errors;
    },
    
    businessAddressStateErrors() {
      const errors = [];
      if (!this.$v.trustInfo.businessAddress.state.$dirty) return errors;
      !this.$v.trustInfo.businessAddress.state.required && errors.push('State is required');
      return errors;
    },
    
    businessAddressZipErrors() {
      const errors = [];
      if (!this.$v.trustInfo.businessAddress.zipCode.$dirty) return errors;
      !this.$v.trustInfo.businessAddress.zipCode.required && errors.push('ZIP code is required');
      return errors;
    },
    
    businessAddressCountryErrors() {
      const errors = [];
      if (!this.$v.trustInfo.businessAddress.country.$dirty) return errors;
      !this.$v.trustInfo.businessAddress.country.required && errors.push('Country is required');
      return errors;
    },
  },
  
  methods: {
    formatTaxId() {
      if (!this.trustInfo.taxId) return;
      
      const input = this.trustInfo.taxId;
      const cleaned = input.replace(/\D/g, '');
      
      if (this.trustInfo.taxIdType === 'SSN') {
        const formatted = cleaned.replace(/^(\d{3})(\d{2})(\d{0,4}).*/, (match, p1, p2, p3) => {
          if (p3) return `${p1}-${p2}-${p3}`;
          if (p2) return `${p1}-${p2}`;
          return p1;
        });
        this.trustInfo.taxId = formatted;
      } else if (this.trustInfo.taxIdType === 'TIN') {
        const formatted = cleaned.replace(/^(\d{2})(\d{0,7}).*/, (match, p1, p2) => {
          if (p2) return `${p1}-${p2}`;
          return p1;
        });
        this.trustInfo.taxId = formatted;
      }
      
      this.$v.trustInfo.taxId.$touch();
    },
    
    standardizeDateOfFormation() {
      this.$v.trustInfo.dateOfFormation.$touch();
      if (validInputDate(this.trustInfo.dateOfFormation)) {
        this.trustInfo.dateOfFormation = this.trustInfo.dateOfFormation
          ? window
              .moment(this.trustInfo.dateOfFormation, INPUT_DATE_FMT, true)
              .format(US_DATE_SHORT)
          : null;
      }
    },
    
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];
      
      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }
      
      const data = {
        instance: this.instance,
        trustInfo: {
          ...this.trustInfo,
          dateOfFormation: this.trustInfo.dateOfFormation 
            ? formatDate(this.trustInfo.dateOfFormation, US_DATE_SHORT) 
            : null
        }
      };
      
      this.completeStep(MENUS.CLIENT.PERSHING_TRUST_INFO.id, data, this.instance)
        .then(() => {
          this.goNext(to);
        })
        .catch(() => {});
    },
    
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },
    
    skip() {
      this.goNext(this.pages[this.pageIndex + 1]);
    },
    
    leave() {
      const route = this.leaveRoute;
      
      const data = {
        instance: this.instance,
        trustInfo: {
          ...this.trustInfo,
          dateOfFormation: this.trustInfo.dateOfFormation 
            ? formatDate(this.trustInfo.dateOfFormation, US_DATE_SHORT) 
            : null
        }
      };
      
      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(MENUS.CLIENT.PERSHING_TRUST_INFO.id, this.instance);
      
      this.completeStep(MENUS.CLIENT.PERSHING_TRUST_INFO.id, data, this.instance)
        .then(() => {
          this.goNext(route);
        })
        .catch(() => {});
    },
  },
  
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex;
    let i = 0;
    for (const page of this.pages) {
      if (typeof page === 'object' && window.location.pathname.includes(page.pathname.replaceAll(' ', '%20'))) {
        this.pageIndex = i;
      }
      i++;
    }
    
    this.formHidden = this.isPershingTrustInfoComplete;
  },
};
</script>

<style lang="scss" scoped>
.cal-icon {
  line-height: 32px;
  margin-right: 8px;
}

.syncing-message {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}

.step-form-container {
  &.large {
    max-width: 800px;
  }
}

.help-link {
  padding-top: 8px;
  
  a {
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      text-decoration: none;
    }
  }
}

.skip-for-now {
  margin-top: 20px;
  text-align: center;
  
  a {
    color: #666;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      text-decoration: none;
    }
  }
}
</style>
